import { forwardRef, ChangeEvent, useState } from "react";
import { FieldError, useFormContext } from "react-hook-form";
import { StyledInput } from "../form/FormElementStyles";
import { Container, StyledFormLabel } from "./CommonStyles";
import styled from "@emotion/styled";
import { InputBaseComponentProps } from "@mui/material";

export interface TimePointFieldProps {
    inline?: boolean;
    label: string;
    id: string;
    description?: string;
    placeholder?: string;
    value?: string;
    error?: FieldError;
    errorMessage?: string;
    required?: boolean;
    type?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    additionalInfo?: string;
    fontWeightBold?: boolean;
    /**
     * when not providing a label, use this to directly specify the aria-label attribute
     */
    inputProps?: InputBaseComponentProps;

    disabled?: boolean;
}

const StyledTimePointInput = styled(StyledInput)(({ theme }) => ({
    "&.timepoint": {
        width: 64,
    },
}));

/**
 * text input field for edit entry form
 */
export const TimePointField = forwardRef<any, TimePointFieldProps>((props: TimePointFieldProps, ref) => {
    const {
        label,
        id,
        placeholder,
        required,
        type = "text",
        description,
        additionalInfo,
        fontWeightBold,
        inputProps,
        inline,
        disabled,
    } = props;
    // local value state to make sure only valid inputs are passed to the onChange handler
    const [localValue, setLocalValue] = useState(props.value || "");
    const {
        register,
        formState: { errors },
    } = useFormContext();

    const {
        ref: componentRef,
        name,
        onChange,
        onBlur,
    } = register(
        id as "dummy",
        {
            validate: (val) => {
                const requiredResult = !required || !!val.trim();
                // value should match regex for [h:]mm:ss
                const regex = /^(\d{1,2}:)?([0-5][0-9]:)?([0-5][0-9])$/;
                const patternResult = val ? regex.test(val) : true;
                return requiredResult && patternResult;
            },
        }
    );

    // define a local change handler for the input field, call the onChange received from register() there
    // and then call the onChange from props
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        /* this regex is different from the one in validation!
         * we allow input values that are "on the way" to become valid values, like 4:5, so the user can type 4:56.
         * for the validation, values like 4:5 are not valid.
         */
        const regex = /^(\d*:)?([0-5]?[0-9]:)?([0-5]?[0-9])?$/;
        if (regex.test(value)) {
            setLocalValue(value);
            // Call the onChange from register
            onChange(event);
        }
        else {
            event.target.value = localValue;
        }
    };

    return (
        <Container inline={inline}>
            {label && (
                <StyledFormLabel
                    htmlFor={id}
                    required={required}
                    text={label}
                    description={description}
                    additionalInfo={additionalInfo}
                    fontWeightBold={fontWeightBold}
                />
            )}
            <StyledTimePointInput
                classes={{ root: "timepoint" }}
                id={id}
                error={!!errors[id]}
                inputRef={ref}
                type={type}
                aria-invalid={errors[id] ? "true" : "false"}
                placeholder={placeholder}
                required={required}
                ref={componentRef}
                name={name}
                onChange={handleChange}
                onBlur={onBlur}
                disabled={disabled}
                inputProps={inputProps}
            />
        </Container>
    );
});
